import { useState } from 'react';

const steps = [
  {
    num: '01',
    icon: 'ri-search-line',
    title: 'Google öffnen',
    desc: 'Einfach auf den Button klicken — Google Maps öffnet sich direkt auf unserem Profil.',
  },
  {
    num: '02',
    icon: 'ri-star-line',
    title: 'Sterne vergeben',
    desc: 'Wählen Sie Ihre Bewertung und schildern Sie kurz Ihre Erfahrung mit uns.',
  },
  {
    num: '03',
    icon: 'ri-send-plane-line',
    title: 'Absenden & fertig',
    desc: 'Ihre Bewertung hilft anderen Kunden und stärkt lokale Handwerksbetriebe.',
  },
];

function GoogleColorLogo() {
  return (
    <span className="inline-flex items-center font-black text-2xl leading-none tracking-tight select-none">
      <span className="text-[#4285F4]">G</span>
      <span className="text-[#EA4335]">o</span>
      <span className="text-[#FBBC05]">o</span>
      <span className="text-[#4285F4]">g</span>
      <span className="text-[#34A853]">l</span>
      <span className="text-[#EA4335]">e</span>
    </span>
  );
}

function StarDisplay({ count = 5 }: { count?: number }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill text-[#FBBC04] text-xl" />
      ))}
    </div>
  );
}

export default function GoogleReviewSection() {
  const [hovered, setHovered] = useState(false);

  return (
    <section className="py-0 bg-[#F8F7F4]">
      {/* Main banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-[#0D2B4E]">
          {/* Decorative circles */}
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/[0.03] pointer-events-none" />
          <div className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full bg-white/[0.03] pointer-events-none" />
          <div className="absolute top-1/2 right-1/4 w-32 h-32 rounded-full bg-[#C8A96E]/10 pointer-events-none" />

          <div className="relative z-10 px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
            <div className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center">

              {/* Left — main content */}
              <div>
                {/* Label */}
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 rounded-full bg-[#FBBC04]/20 flex items-center justify-center">
                    <i className="ri-star-fill text-[#FBBC04] text-sm" />
                  </div>
                  <span className="text-[#C8A96E] text-xs font-bold tracking-widest uppercase">
                    Ihre Meinung zählt
                  </span>
                </div>

                {/* Headline */}
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
                  Waren Sie zufrieden?<br />
                  <span className="text-[#C8A96E]">Teilen Sie Ihre Erfahrung</span>
                  <br />
                  <span className="text-white/80 text-xl sm:text-2xl lg:text-3xl font-semibold">auf </span>
                  <GoogleColorLogo />
                </h2>

                <p className="text-white/60 text-sm sm:text-base leading-relaxed max-w-xl mb-8">
                  Jede Bewertung hilft uns, anderen Kunden in Ratingen und NRW eine bessere
                  Entscheidungsgrundlage zu geben — und stärkt lokales Handwerk.
                </p>

                {/* Steps */}
                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                  {steps.map((s) => (
                    <div
                      key={s.num}
                      className="flex sm:flex-col gap-4 sm:gap-3 bg-white/[0.06] rounded-2xl p-4 sm:p-5 border border-white/10"
                    >
                      <div className="flex sm:flex-row items-center gap-3 sm:gap-2 flex-shrink-0">
                        <span className="text-xs font-bold text-[#C8A96E] w-6">{s.num}</span>
                        <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${s.icon} text-white text-base`} />
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-white text-sm mb-1">{s.title}</p>
                        <p className="text-white/50 text-xs leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <a
                  href="https://g.page/r/igmfacilityservice/review"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  className={`inline-flex items-center gap-3 px-7 py-4 rounded-xl font-bold text-base transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    hovered
                      ? 'bg-white text-[#0D2B4E] scale-[1.02]'
                      : 'bg-[#C8A96E] text-white'
                  }`}
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-fill text-lg" />
                  </div>
                  Jetzt auf Google bewerten
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </div>
                </a>

                <p className="text-white/30 text-xs mt-3">
                  Dauert nur 60 Sekunden · Kein Konto erforderlich
                </p>
              </div>

              {/* Right — floating rating card */}
              <div className="hidden lg:flex flex-col items-center gap-6">
                {/* Big rating badge */}
                <div className="bg-white rounded-3xl p-8 text-center w-64 relative">
                  <div className="absolute -top-3 -right-3 bg-[#34A853] text-white text-xs font-bold px-3 py-1 rounded-full">
                    Top bewertet
                  </div>

                  {/* Google wordmark */}
                  <div className="flex items-center justify-center gap-0.5 mb-4">
                    {['G','o','o','g','l','e'].map((l, i) => {
                      const colors = ['#4285F4','#EA4335','#FBBC05','#4285F4','#34A853','#EA4335'];
                      return (
                        <span
                          key={i}
                          className="font-black text-lg leading-none"
                          style={{ color: colors[i] }}
                        >
                          {l}
                        </span>
                      );
                    })}
                  </div>

                  {/* Score */}
                  <div className="text-6xl font-black text-gray-900 leading-none mb-2">5,0</div>
                  <StarDisplay count={5} />
                  <p className="text-gray-400 text-sm mt-2">aus 15 Bewertungen</p>

                  {/* Mini avatars row */}
                  <div className="flex items-center justify-center mt-4 -space-x-2">
                    {[
                      { initials: 'ON', color: 'bg-emerald-500' },
                      { initials: 'MS', color: 'bg-sky-500' },
                      { initials: 'LH', color: 'bg-violet-500' },
                      { initials: 'LR', color: 'bg-teal-500' },
                    ].map((a) => (
                      <div
                        key={a.initials}
                        className={`w-8 h-8 ${a.color} rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold`}
                      >
                        {a.initials}
                      </div>
                    ))}
                    <div className="w-8 h-8 bg-gray-100 rounded-full border-2 border-white flex items-center justify-center text-gray-500 text-[10px] font-bold">
                      +11
                    </div>
                  </div>
                </div>

                {/* Trust badges */}
                <div className="flex flex-col gap-2 w-64">
                  {[
                    { icon: 'ri-shield-check-line', text: 'Verifizierte Bewertungen' },
                    { icon: 'ri-map-pin-line', text: 'Standort: Ratingen, NRW' },
                    { icon: 'ri-award-line', text: 'Seit 2010 im Einsatz' },
                  ].map((b) => (
                    <div key={b.text} className="flex items-center gap-2.5 bg-white/[0.06] rounded-xl px-4 py-2.5 border border-white/10">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${b.icon} text-[#C8A96E] text-base`} />
                      </div>
                      <span className="text-white/70 text-xs font-medium">{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
